import type { DocMeta } from '@/doc-sync/doc-store';
import { DEFAULT_MARGINS, PAPER_DIMENSIONS, marginsFit } from '@/pattern-composer/model/paper';
import type { Margins, PageSetup, PaperSize } from '@/pattern-composer/model/paper';
import { isPatternLanguage } from '@/pattern-composer/model/pattern-language';
import type { PatternLanguage } from '@/pattern-composer/model/pattern-language';
import { LEGEND_LIMITS, MAX_FLOW_CHARS, MAX_FLOW_OFFSET, MIN_ELEMENT_WIDTH, createEmptyDocument } from '@/pattern-composer/model/types';
import type { Page, PatternDocument } from '@/pattern-composer/model/types';
import { MAX_FLOW_PAGES, MAX_FLOW_UNITS } from '@/pattern-composer/text-flow/plan-flow';

import { upgradeV2 } from './upgrade-v2';

/** Pattern documents carry no per-type gallery metadata (no `kind`). */
export type PatternMetaExtra = Record<never, never>;
export type PatternMeta = DocMeta<PatternMetaExtra>;

/** App-level persistence envelope for the pattern composer. The `document`
 *  payload is the composer's own model (src/pattern-composer/model/types.ts);
 *  Lexical editorState inside text elements is deliberately opaque here —
 *  Lexical owns that schema. */
export type StoredPatternV3 = {
	formatVersion: 3;
	id: string;
	name: string;
	createdAt: number;
	updatedAt: number;
	document: PatternDocument;
};

export type StoredPattern = StoredPatternV3;

/** The shape v2 wrote: the same document minus the pattern language, and no legend elements.
 *  Only ever read, never written: `parseStoredPattern` upgrades it in memory. */
export type StoredPatternV2 = Omit<StoredPatternV3, 'formatVersion' | 'document'> & {
	formatVersion: 2;
	document: { setup: Omit<PageSetup, 'language'>; pages: Page[] };
};

/** The newest `formatVersion` this app reads and writes. A stored pattern above it was written by a newer app. */
export const NEWEST_PATTERN_FORMAT_VERSION = 3;

/** The shape v1 wrote: no `setup` on the document, and a `size` on every page (always 'letter').
 *  Only ever read, never written: `parseStoredPattern` upgrades it in memory. */
type StoredPatternV1 = Omit<StoredPatternV2, 'formatVersion' | 'document'> & {
	formatVersion: 1;
	document: { pages: Array<Page & { size: 'letter' }> };
};

export class StoredPatternError extends Error {}

/** The pattern was written by a newer app than this one: it is intact, just not readable here. */
export class StoredPatternNewerError extends StoredPatternError {
	readonly formatVersion: number;
	constructor(formatVersion: number) {
		super(`stored pattern formatVersion ${formatVersion} is newer than this app reads`);
		this.formatVersion = formatVersion;
	}
}

const ASSET_ID_RE = /^[a-f0-9]{64}$/;

export function newStoredPattern(name: string, paperSize?: PaperSize, language?: PatternLanguage): StoredPattern {
	const now = Date.now();
	return {
		formatVersion: NEWEST_PATTERN_FORMAT_VERSION,
		id: crypto.randomUUID(),
		name,
		createdAt: now,
		updatedAt: now,
		document: createEmptyDocument(paperSize, language),
	};
}

const fail = (msg: string): never => {
	throw new StoredPatternError(msg);
};

function checkAssetRef(e: Record<string, unknown>, what: string): void {
	if (typeof e.naturalAspect !== 'number') fail(`${what} naturalAspect must be a number`);
	if (typeof e.assetId === 'string') {
		if (!ASSET_ID_RE.test(e.assetId)) fail(`${what} assetId must be 64-hex`);
		return;
	}
	if (typeof e.src === 'string') return; // legacy inline data URL, opaque
	fail(`${what} must carry assetId or src`);
}

function checkDiagramSource(raw: unknown): void {
	if (typeof raw !== 'object' || raw == null) fail('diagram source must be an object');
	const s = raw as Record<string, unknown>;
	if (typeof s.diagramId !== 'string') fail('diagram source diagramId must be a string');
	if (s.kind !== 'crochet' && s.kind !== 'knitting') fail(`unsupported diagram source kind: ${String(s.kind)}`);
	const r = s.worldRect;
	if (typeof r !== 'object' || r == null) fail('diagram source worldRect must be an object');
	const rect = r as Record<string, unknown>;
	if (
		typeof rect.x !== 'number' || typeof rect.y !== 'number'
		|| typeof rect.width !== 'number' || typeof rect.height !== 'number'
	) {
		fail('diagram source worldRect must be numeric');
	}
	// Export options are the canvas-export contract; validate shape only.
	const o = s.options;
	if (typeof o !== 'object' || o == null) fail('diagram source options must be an object');
	const opts = o as Record<string, unknown>;
	if (
		typeof opts.includeGrid !== 'boolean' || typeof opts.includeGuides !== 'boolean'
		|| typeof opts.includeLabels !== 'boolean' || typeof opts.transparent !== 'boolean'
		|| (opts.scale !== 1 && opts.scale !== 2 && opts.scale !== 3)
	) {
		fail('diagram source options are malformed');
	}
}

/** `flowBreaks` is the layout's cache of where a text box's later pages start: an array of integer unit indices
 *  >= 1 in non-decreasing order. It is bounded too (S6-20), so a hostile pattern can't make the editor append
 *  thousands of pages: fewer than `MAX_FLOW_PAGES` entries, none above `MAX_FLOW_UNITS`. */
function checkFlowBreaks(raw: unknown): void {
	const message = 'text flowBreaks must be non-decreasing integers >= 1';
	if (!Array.isArray(raw) || raw.length >= MAX_FLOW_PAGES) fail(message);
	const breaks = raw as unknown[];
	let previous = 1;
	for (let i = 0; i < breaks.length; i++) {
		const value = breaks[i];
		if (typeof value !== 'number' || !Number.isInteger(value) || value < previous || value > MAX_FLOW_UNITS) fail(message);
		previous = value as number;
	}
}

/** `flowBreakOffsets`: null or `{ unit, offset }` per entry (integer unit 0..MAX_FLOW_UNITS, integer offset 1..MAX_FLOW_CHARS).
 *  Its length need not match `flowBreaks`: `effectiveBreaks` sanitises that on read. */
function checkFlowBreakOffsets(raw: unknown): void {
	const message = 'text flowBreakOffsets must be null or { unit, offset } integers';
	if (!Array.isArray(raw) || raw.length >= MAX_FLOW_PAGES) fail(message);
	for (const entry of raw as unknown[]) {
		if (entry === null) continue;
		if (typeof entry !== 'object') fail(message);
		const { unit, offset } = entry as Record<string, unknown>;
		if (typeof unit !== 'number' || !Number.isInteger(unit) || unit < 0 || unit > MAX_FLOW_UNITS) fail(message);
		if (typeof offset !== 'number' || !Number.isInteger(offset) || offset < 1 || offset > MAX_FLOW_CHARS) fail(message);
	}
}

/** `flowOverrides`: null or `{ dx?, dy?, width? }` per continuation; dx and dy within +-MAX_FLOW_OFFSET,
 *  width within MIN_ELEMENT_WIDTH..MAX_FLOW_OFFSET. */
function checkFlowOverrides(raw: unknown): void {
	const message = 'text flowOverrides must be null or { dx?, dy?, width? } within bounds';
	if (!Array.isArray(raw) || raw.length >= MAX_FLOW_PAGES) fail(message);
	const inRange = (value: unknown, min: number): boolean =>
		value === undefined || (typeof value === 'number' && Number.isFinite(value) && value >= min && value <= MAX_FLOW_OFFSET);
	for (const entry of raw as unknown[]) {
		if (entry === null) continue;
		if (typeof entry !== 'object' || Array.isArray(entry)) fail(message);
		const { dx, dy, width } = entry as Record<string, unknown>;
		if (!inRange(dx, -MAX_FLOW_OFFSET) || !inRange(dy, -MAX_FLOW_OFFSET) || !inRange(width, MIN_ELEMENT_WIDTH)) fail(message);
	}
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** A string no longer than `max` (and, with `nonEmpty`, not empty). */
function checkBoundedString(value: unknown, max: number, what: string, nonEmpty = false): void {
	if (typeof value !== 'string' || value.length > max || (nonEmpty && value.length === 0)) {
		fail(`${what} must be a string of ${nonEmpty ? '1' : '0'}..${max} characters`);
	}
}

/** The diagram a generated element was made from: `{ diagramId, kind }` and nothing about geometry. */
function checkGeneratedSource(raw: unknown, what: string): void {
	if (!isRecord(raw)) fail(`${what} source must be an object`);
	const s = raw as Record<string, unknown>;
	checkBoundedString(s.diagramId, LEGEND_LIMITS.diagramId, `${what} source diagramId`);
	if (s.kind !== 'crochet' && s.kind !== 'knitting') fail(`${what} source kind must be crochet or knitting`);
}

function checkLegend(e: Record<string, unknown>): void {
	checkGeneratedSource(e.source, 'legend');
	checkBoundedString(e.title, LEGEND_LIMITS.title, 'legend title');
	checkBoundedString(e.hash, LEGEND_LIMITS.hash, 'legend hash');
	if (!Array.isArray(e.rows) || e.rows.length > LEGEND_LIMITS.rows) {
		fail(`legend rows must be an array of at most ${LEGEND_LIMITS.rows}`);
	}
	for (const row of e.rows as unknown[]) {
		if (!isRecord(row)) fail('legend row must be an object');
		const r = row as Record<string, unknown>;
		checkBoundedString(r.stitchId, LEGEND_LIMITS.stitchId, 'legend row stitchId', true);
		checkBoundedString(r.abbrev, LEGEND_LIMITS.abbrev, 'legend row abbrev');
		checkBoundedString(r.name, LEGEND_LIMITS.name, 'legend row name');
	}
}

function checkGenerated(raw: unknown): void {
	if (!isRecord(raw)) fail('text generated must be an object');
	const g = raw as Record<string, unknown>;
	checkGeneratedSource(g.source, 'text generated');
	checkBoundedString(g.hash, LEGEND_LIMITS.hash, 'text generated hash');
}

/** `version` is the format the element was read from: legends and `text.generated` only exist from v3 on. */
function checkElement(raw: unknown, version: 2 | 3): void {
	if (typeof raw !== 'object' || raw == null) fail('element is not an object');
	const e = raw as Record<string, unknown>;
	if (typeof e.id !== 'string') fail('element id must be a string');
	if (
		typeof e.x !== 'number' || typeof e.y !== 'number'
		|| typeof e.width !== 'number' || typeof e.height !== 'number'
	) {
		fail('element geometry must be numeric');
	}
	if (e.type === 'text') {
		if (e.editorState !== null && (typeof e.editorState !== 'object' || e.editorState == null)) {
			fail('text editorState must be an object or null');
		}
		if (e.flowBreaks !== undefined) checkFlowBreaks(e.flowBreaks);
		if (e.flowBreakOffsets !== undefined) checkFlowBreakOffsets(e.flowBreakOffsets);
		if (e.flowOverrides !== undefined) checkFlowOverrides(e.flowOverrides);
		if (e.generated !== undefined && version >= 3) checkGenerated(e.generated);
		return;
	}
	if (e.type === 'image') {
		checkAssetRef(e, 'image');
		return;
	}
	if (e.type === 'diagram') {
		checkAssetRef(e, 'diagram');
		checkDiagramSource(e.source);
		return;
	}
	if (e.type === 'legend' && version >= 3) {
		checkLegend(e);
		return;
	}
	fail(`unknown element type: ${String(e.type)}`);
}

const MARGIN_SIDES = ['top', 'right', 'bottom', 'left'] as const;

/** Paper size must be a known one, every margin a finite number >= 0, and the margins must leave
 *  some room on the paper: a hostile or corrupt setup never reaches the editor. */
function checkSetup(raw: unknown, version: 2 | 3): void {
	if (typeof raw !== 'object' || raw == null) fail('document.setup must be an object');
	const setup = raw as Record<string, unknown>;
	if (version >= 3 && !isPatternLanguage(setup.language)) fail('setup.language must be en, ja or zh-TW');
	const paperSize = setup.paperSize;
	if (typeof paperSize !== 'string' || !Object.hasOwn(PAPER_DIMENSIONS, paperSize)) {
		fail(`unsupported paperSize: ${String(paperSize)}`);
	}
	const marginsRaw = setup.margins;
	if (typeof marginsRaw !== 'object' || marginsRaw == null) fail('setup.margins must be an object');
	const margins = marginsRaw as Record<string, unknown>;
	for (const side of MARGIN_SIDES) {
		const value = margins[side];
		if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
			fail(`setup.margins.${side} must be a finite number >= 0`);
		}
	}
	if (!marginsFit(paperSize as PaperSize, margins as Margins)) {
		// marginsFit decides; which axis ran out of room only picks the message.
		const acrossTheWidth =
			(margins.left as number) + (margins.right as number) >= PAPER_DIMENSIONS[paperSize as PaperSize].width;
		fail(`setup.margins leave no room ${acrossTheWidth ? 'across' : 'down'} the page`);
	}
}

/** A page's own fields: `id` and `elements`, each element checked. */
function checkPage(page: unknown, version: 2 | 3, extra?: (p: Record<string, unknown>) => void): void {
	if (typeof page !== 'object' || page == null) fail('page is not an object');
	const p = page as Record<string, unknown>;
	if (typeof p.id !== 'string') fail('page id must be a string');
	extra?.(p);
	if (!Array.isArray(p.elements)) fail('page.elements must be an array');
	for (const el of p.elements as unknown[]) checkElement(el, version);
}

/** v1 only ever had Letter, so the upgrade is exact: the default setup, and the pages minus their `size`. */
function upgradeV1(v1: StoredPatternV1): StoredPatternV2 {
	return {
		...v1,
		formatVersion: 2,
		document: {
			setup: { paperSize: 'letter', margins: { ...DEFAULT_MARGINS } },
			pages: v1.document.pages.map(({ size: _size, ...page }) => page),
		},
	};
}

/** The single load funnel. Envelope + document structure checked by hand;
 *  Lexical editorState interiors stay opaque. Older formats are upgraded here, in memory — the
 *  next save writes the newest one. Anything suspect throws —
 *  callers must treat a throw as "do not touch the stored document". */
export function parseStoredPattern(raw: unknown): StoredPattern {
	if (typeof raw !== 'object' || raw == null) fail('stored pattern is not an object');
	const r = raw as Record<string, unknown>;
	const version = r.formatVersion;
	if (typeof version === 'number' && Number.isFinite(version) && version > NEWEST_PATTERN_FORMAT_VERSION) {
		throw new StoredPatternNewerError(version);
	}
	if (version !== 1 && version !== 2 && version !== 3) fail(`unsupported formatVersion: ${String(version)}`);
	if (
		typeof r.id !== 'string'
		|| typeof r.name !== 'string'
		|| typeof r.createdAt !== 'number'
		|| typeof r.updatedAt !== 'number'
	) {
		fail('malformed envelope metadata');
	}
	const document = r.document;
	if (typeof document !== 'object' || document == null) fail('document must be an object');
	const doc = document as Record<string, unknown>;
	const pages = doc.pages;
	if (!Array.isArray(pages) || pages.length === 0) fail('document.pages must be a non-empty array');
	if (version === 1) {
		// v1: every page carries a `size`, and it was only ever 'letter'.
		for (const page of pages as unknown[]) {
			checkPage(page, 2, (p) => {
				if (p.size !== 'letter') fail(`unsupported page size: ${String(p.size)}`);
			});
		}
		return upgradeV2(upgradeV1(raw as StoredPatternV1));
	}
	const known: 2 | 3 = version === 2 ? 2 : 3;
	checkSetup(doc.setup, known);
	for (const page of pages as unknown[]) checkPage(page, known);
	return known === 2 ? upgradeV2(raw as StoredPatternV2) : (raw as StoredPattern);
}

/** Gallery-list entry from a raw (unparsed) IndexedDB record, or null when
 *  too corrupt to summarize — list() deliberately skips the full parse so one
 *  corrupt document can't blank the gallery. */
export function patternMetaFromRecord(
	raw: unknown,
	thumbById: ReadonlyMap<string, Blob>,
): PatternMeta | null {
	if (typeof raw !== 'object' || raw == null) return null;
	const d = raw as Record<string, unknown>;
	if (
		typeof d.id !== 'string'
		|| typeof d.name !== 'string'
		|| typeof d.createdAt !== 'number'
		|| typeof d.updatedAt !== 'number'
	) {
		return null;
	}
	return {
		id: d.id,
		name: d.name,
		createdAt: d.createdAt,
		updatedAt: d.updatedAt,
		thumbnail: thumbById.get(d.id) ?? null,
	};
}
